import { ChartBar, ChevronRight } from "lucide-react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import AppContext from "src/components/AppContext";
import { DataPoint, fetchWrapper } from "src/helpers";
import {
  formatNumber,
  getDisplayLabel,
  getLocalStorageUser,
  NetPercentBadge,
} from "src/helpers/api/sharedFunction";
import { getSortIcon } from "src/helpers/api/sortconfig";
import { getFormattedDate } from "src/helpers/dateFormatter";
import SkeletonRow from "../skeleton/substancialSkeleton";
import InsiderChartModal from "./chartModal";
import { useSocket } from "src/components/SocketContext";
import InfoIcon from "src/assets/images/info.svg";
import {
  DropdownOption as FilterOption,
  MultiSelectDropdown,
} from "./InsiderFilter";
import { formatDate } from "src/helpers/api/sharedFunction";
import Image from "next/image";

export interface SubstantialTrade {
  id?: number | string;
  company_name: string;
  stock_symbol: string;
  broadcast_date: string;
  segment: string;
  total_price: number;
  share_count: number;
  holding_change: number;
  person_category?: string;
  transaction_type?: string;
  acquisition_mode?: string;
  transactions?: SubstantialTransaction[];
}

export interface SubstantialTransactionApi {
  acquirer_disposer_name: string;
  person_category?: string;
  total_price: number;
  share_count: number;
  acquisition_from_date: string;
  acquisition_to_date: string;
  transaction_type: string;
  holding_change: number;
}

export interface SubstantialTransaction {
  id?: number | string;
  personnel: string;
  position: string;
  amount: number;
  shares: number;
  acquisition_from: string;
  acquisition_to: string;
  transaction_type: "BUY" | "SELL" | string;
  holding_change: number;
}

const SubstantialActivity = () => {
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const user = getLocalStorageUser() || {};
  const token = user?.token;
  const [data, setData] = useState<SubstantialTrade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc" | null;
  }>({
    key: "",
    direction: null,
  });
  const [selectSymbol, setSelectSymbol] = useState<string[]>([]);
  const [selectedMarketSegment, setSelectedMarketSegment] = useState<string[]>(
    [],
  );
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [symbolOptions, setSymbolOptions] = useState<FilterOption[]>([]);
  const [marketSegmentOptions, setMarketSegmentOptions] = useState<
    FilterOption[]
  >([]);
  const [categoryOptions, setCategoryOptions] = useState<FilterOption[]>([]);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedData, setExpandedData] = useState<
    Record<string, SubstantialTransaction[]>
  >({});
  const [loadingRow, setLoadingRow] = useState<Record<string, boolean>>({});

  // Chart Modal State
  const [selectedSymbolName, setSelectedSymbolName] = useState<string>("");
  const [aquisitionFromDate, setAquisitionFromDate] = useState<string>("");
  const [aquisitionToDate, setAquisitionToDate] = useState<string>("");
  const [subListingtransactionType, setSubListingTransactionType] =
    useState<string>("");
  const [candledata, setCandleData] = useState<DataPoint[]>([]);
  const { emit, isConnected } = useSocket();
  const [candleLoading, setCandleLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);

  const observerTarget = useRef<HTMLTableRowElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef<boolean>(false);

  const getCandleChartData = async (symbol: string) => {
    try {
      setCandleLoading(true);
      const response = await fetchWrapper.get(
        `/api/dashboard/ohlcdata?name=${encodeURIComponent(symbol)}`,
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      setCandleData(response?.data);
    } finally {
      setCandleLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSymbolName("");
  };

  useEffect(() => {
    if (!selectedSymbolName || !isConnected) return;
    emit("subscribe_stock", { symbol: [selectedSymbolName] });
    return () => {
      emit("unsubscribe_stock", { symbol: [selectedSymbolName] });
    };
  }, [selectedSymbolName, isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildQueryParams = useCallback(
    (page: number) => {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", "20");
      if (sortConfig.key) params.set("sortColumn", sortConfig.key);
      if (sortConfig.direction) params.set("sortOrder", sortConfig.direction);
      if (selectSymbol.length) params.set("symbols", selectSymbol.join(","));
      if (selectedMarketSegment.length)
        params.set("marketSegments", selectedMarketSegment.join(","));
      if (selectedCategory.length)
        params.set("categories", selectedCategory.join(","));
      return params.toString();
    },
    [sortConfig, selectSymbol, selectedMarketSegment, selectedCategory],
  );

  const fetchData = useCallback(
    async (isLoadMore = false, pageParam = 1) => {
      try {
        setIsLoading(true);
        isFetchingRef.current = true;
        if (!token) return;
        const qs = buildQueryParams(pageParam);
        const response = await fetchWrapper.get(
          `/api/dashboard/insider/substantial/activity?${qs}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );

        if (response) {
          const items: SubstantialTrade[] = response.data ?? [];

          if (response?.symbols) {
            setSymbolOptions(
              response.symbols.map((s: { name: string; symbol: string }) => ({
                value: s.symbol,
                label: s.name,
              })),
            );
          }
          if (response?.market_caps) {
            setMarketSegmentOptions(
              response.market_caps.map((c: string) => ({
                value: c,
                label: c,
              })),
            );
          }
          if (response?.person_categories) {
            setCategoryOptions(
              response.person_categories.map((c: string) => ({
                value: c,
                label: getDisplayLabel(c),
              })),
            );
          }

          if (isLoadMore) {
            setData((prev) => [...prev, ...items]);
          } else {
            setData(items);
          }
          if (response.pagination !== undefined) {
            setHasMore(response.pagination.hasNextPage);
          } else if (response.hasNextPage !== undefined) {
            setHasMore(response.hasNextPage);
          } else {
            setHasMore(items.length >= 20);
          }
        }
      } catch (err) {
        console.error("SubstantialActivity fetch error:", err);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [buildQueryParams, token],
  );

  const handleLoadMore = useCallback(() => {
    if (!isFetchingRef.current && hasMore && data.length > 0) {
      isFetchingRef.current = true;
      const nextPage = page + 1;
      setPage(nextPage);
      fetchData(true, nextPage);
    }
  }, [hasMore, data, page, fetchData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isFetchingRef.current &&
          data.length > 0
        ) {
          handleLoadMore();
        }
      },
      { threshold: 0.1, root: tableContainerRef.current },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLoadMore, hasMore]);

  useEffect(() => {
    setPage(1);
    setExpandedRows({});
    setData([]);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
    fetchData(true, 1);
  }, [selectSymbol, selectedMarketSegment, selectedCategory, sortConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
              ? null
              : "asc"
          : "asc",
    }));
  };

  const toggleRow = async (rowIndex: string, trade: SubstantialTrade) => {
    const isExpanded = !!expandedRows[rowIndex];
    setExpandedRows((prev) => ({ ...prev, [rowIndex]: !isExpanded }));

    if (!isExpanded && !expandedData[rowIndex] && !loadingRow[rowIndex]) {
      setLoadingRow((prev) => ({ ...prev, [rowIndex]: true }));
      try {
        const qs = new URLSearchParams({
          symbol: trade.stock_symbol || "",
        });

        if (selectedMarketSegment.length > 0)
          qs.set("marketSegments", selectedMarketSegment.join(","));
        if (selectedCategory.length > 0)
          qs.set("categories", selectedCategory.join(","));

        const queryString = qs.toString();

        const response = await fetchWrapper.get(
          `/api/dashboard/insider/substantial/activity?${queryString}`,
          undefined,
          { Authorization: `Bearer ${token}` },
        );

        const txs = (response.data || []).map(
          (t: SubstantialTransactionApi) => ({
            personnel: t.acquirer_disposer_name,
            position: t.person_category || "",
            amount: t.total_price,
            shares: t.share_count,
            acquisition_from: t.acquisition_from_date,
            acquisition_to: t.acquisition_to_date,
            transaction_type: t.transaction_type,
            holding_change: t.holding_change,
          }),
        );

        setExpandedData((prev) => ({ ...prev, [rowIndex]: txs }));
      } catch (err) {
        console.error("fetch expanded tx error", err);
      } finally {
        setLoadingRow((prev) => ({ ...prev, [rowIndex]: false }));
      }
    }
  };

  const resetFilters = () => {
    setSelectSymbol([]);
    setSelectedMarketSegment([]);
    setSelectedCategory([]);
    setPage(1);
    setExpandedData({});
  };

  const toggleDropdown = useCallback((name: string) => {
    setActiveDropdown((prev) => (prev === name ? null : name));
  }, []);

  const hasActiveFilters =
    selectSymbol.length > 0 ||
    selectedMarketSegment.length > 0 ||
    selectedCategory.length > 0;

  const COLUMNS = [
    { label: "Company", key: "company_name", sortable: false },
    { label: "Broadcast Date", key: "broadcast_date", sortable: true },
    { label: "Amount", key: "total_price", sortable: false },
    { label: "Shares", key: "share_count", sortable: false },
    { label: "Net %", key: "holding_change", sortable: true },
    { label: "Segment", key: "market_segment", sortable: false },
    { label: "", key: "chart", sortable: false },
  ] as const;

  return (
    <div className="flex flex-col gap-2 px-1 py-1 sm:px-1 ">
      <div className="flex items-center gap-2">
        <h1 className="text-lg dark:text-white font-semibold">
          Substantial Buy & Sell Activity
        </h1>
        <div className="relative group">
          <Image src={InfoIcon} alt="Info" className="w-4 h-4 cursor-pointer" />
          <div className="absolute z-10 hidden group-hover:block w-55 bg-[#30324e] text-white text-sm p-1 rounded-md shadow-lg border border-[#394564] left-full ml-2">
            substantial buying and selling activity in past 30 days
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-1 py-2">
        <MultiSelectDropdown
          label="Select Stocks"
          options={symbolOptions}
          isSymbol={true}
          selectedValues={selectSymbol}
          onSelect={(v) => {
            setSelectSymbol(v);
            setPage(1);
          }}
          isOpen={activeDropdown === "stocks"}
          onToggle={() => toggleDropdown("stocks")}
        />

        <div className="h-7 w-px bg-gray-200 dark:bg-[#394564] hidden sm:block" />

        <MultiSelectDropdown
          label="Market Segment"
          options={marketSegmentOptions}
          selectedValues={selectedMarketSegment}
          onSelect={(v) => {
            setSelectedMarketSegment(v);
            setPage(1);
          }}
          isOpen={activeDropdown === "market"}
          onToggle={() => toggleDropdown("market")}
        />

        <MultiSelectDropdown
          label="Category"
          options={categoryOptions}
          selectedValues={selectedCategory}
          onSelect={(v) => {
            setSelectedCategory(v);
            setPage(1);
          }}
          isOpen={activeDropdown === "category"}
          onToggle={() => toggleDropdown("category")}
        />

        {hasActiveFilters && (
          <>
            <div className="h-5 w-px bg-gray-200 dark:bg-[#394564] hidden sm:block" />
            <button
              onClick={resetFilters}
              className="cursor-pointer px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-[#00A85F] dark:bg-gradient-to-t dark:from-[#066766] dark:to-[#0FA243] hover:opacity-90 transition whitespace-nowrap shadow-sm"
            >
              Reset Filter
            </button>
          </>
        )}
      </div>

      <div className="bg-white dark:bg-transparent shadow-[5px_1px_5px_rgba(0,0,0,0.09)] rounded-xl overflow-hidden border border-[#d0d4e4] dark:border-[#394564] backdrop-blur-md ">
        <div
          ref={tableContainerRef}
          className="h-[calc(100vh-350px)] overflow-auto"
        >
          <table className="min-w-full w-full relative">
            <colgroup>
              <col className="w-[35%]" /> {/* Company */}
              <col className="w-[13%]" /> {/* Broadcast Date */}
              <col className="w-[13%]" /> {/* Amount */}
              <col className="w-[13%]" /> {/* Shares */}
              <col className="w-[13%]" /> {/* Net % */}
              <col className="w-[12%]" /> {/* Segment */}
              <col className="w-[1%]" /> {/* Chart Action */}
            </colgroup>
            <thead className="sticky top-0 z-20 bg-[#f4f5f8] dark:bg-[#30324e]">
              <tr>
                {COLUMNS.map((col, index) => {
                  const isSortable = "sortable" in col && col.sortable;
                  return (
                    <th
                      key={col.key}
                      onClick={() => isSortable && handleSort(col.key)}
                      className={`text-left py-2 px-2 text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider ${
                        index === 0 ? "pl-6" : ""
                      } ${isSortable ? "cursor-pointer group" : ""} 
                          after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[1px] after:bg-[#d0d4e4] dark:after:bg-[#394564]
                          before:content-[''] before:absolute before:left-0 before:top-0 before:w-full before:h-[1px] before:bg-[#d0d4e4] dark:before:bg-[#394564]
                        overflow-hidden relative`}
                    >
                      <div className="relative flex items-center bg-[#f4f5f8] dark:bg-[#30324e]">
                        {col.label}

                        {isSortable && getSortIcon(sortConfig, col.key)}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {isLoading && data.length === 0 ? (
                <SkeletonRow />
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400 text-sm font-medium">
                      No substantial activity found for the selected filters.
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((trade, i: React.Key | null | undefined) => {
                  const rowKey = `${trade.stock_symbol}-${trade.broadcast_date}-${trade.person_category}-${i}`;
                  const isExpanded = !!expandedRows[rowKey];
                  const transactions = expandedData[rowKey] || [];
                  const isTxLoading = !!loadingRow[rowKey];

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className="border-b border-[#d0d4e4] dark:border-[#394564] hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                        onClick={() => toggleRow(rowKey, trade)}
                      >
                        <td className="py-2 px-2  text-sm">
                          <div className="flex items-start">
                            <ChevronRight
                              className={`h-4 w-4 mr-2 mt-1 transform transition-transform ${
                                isExpanded ? "rotate-90" : ""
                              }`}
                            />
                            <div>
                              <div className="flex items-center">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {trade.company_name}
                                </div>
                                <div className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                  {trade.stock_symbol}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="py-2 px-2  text-sm">
                          {trade.broadcast_date
                            ? getFormattedDate(trade.broadcast_date)
                            : "—"}
                        </td>

                        <td className="py-2 px-2  text-sm">
                          {formatNumber(trade.total_price)}
                        </td>

                        <td className="py-2 px-2  text-sm">
                          {trade.share_count?.toLocaleString() || "0"}
                        </td>

                        <td className="py-2 px-2">
                          <NetPercentBadge value={trade.holding_change} />
                        </td>
                        <td className="py-2 px-2  text-sm">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium  max-w-max ${trade.segment === "-" ? "" : "dark:text-blue-200 text-blue-800 dark:bg-blue-900  bg-blue-100"}`}
                          >
                            {getDisplayLabel(trade.segment) || "—"}
                          </span>
                        </td>
                        <td className="py-2 px-2"></td>
                      </tr>

                      {isExpanded && isTxLoading && (
                        <tr className="bg-gray-50 dark:bg-gray-800/50">
                          <td
                            colSpan={7}
                            className="py-2 px-8 text-xs text-gray-500 font-medium"
                          >
                            Loading details...
                          </td>
                        </tr>
                      )}
                      {isExpanded &&
                        !isTxLoading &&
                        transactions.length > 0 && (
                          <>
                            <tr className="bg-white dark:bg-transparent border-b border-gray-200 dark:border-gray-800">
                              <td className="py-2 pl-10 text-sm  text-gray-500 dark:text-gray-300 tracking-wider">
                                Personnel / Category
                              </td>
                              <td className="py-2 px-2 text-sm  text-gray-500 dark:text-gray-300 tracking-wider">
                                Exec. Date
                              </td>
                              <td className="py-2 px-2 text-sm  text-gray-500 dark:text-gray-300 tracking-wider">
                                Amount
                              </td>
                              <td className="py-2  px-2 text-sm  text-gray-500 dark:text-gray-300 tracking-wider">
                                Shares
                              </td>
                              <td className="py-2 px-3 text-sm  text-gray-500 dark:text-gray-300 tracking-wider">
                                Type
                              </td>
                              <td className="py-2 px-3"></td>
                              <td className="py-2 px-3"></td>
                            </tr>

                            {transactions
                              .filter(
                                (tx) =>
                                  selectedCategory.length === 0 ||
                                  selectedCategory.includes(tx.position),
                              )
                              .map((tx, idx) => {
                                const isLast = idx === transactions.length - 1;
                                const isAmountZero =
                                  !tx.amount || tx.amount === 0;
                                return (
                                  <tr
                                    key={`${rowKey}-tx-${idx}`}
                                    className={`bg-white dark:bg-transparent hover:bg-slate-200/50 dark:hover:bg-[#20232A] transition-colors ${isLast ? "border-b border-[#d0d4e4] dark:border-[#394564]" : "border-b border-gray-100 dark:border-gray-800/60"}`}
                                  >
                                    <td className="py-1 px-2 relative align-middle">
                                      {/* Tree connector vertical line */}
                                      <div
                                        className={`absolute left-6 top-0 w-px bg-gray-300 dark:bg-gray-700 ${isLast ? "bottom-1/2" : "bottom-0"}`}
                                      ></div>
                                      {/* Tree connector horizontal line */}
                                      <div className="absolute left-6 top-1/2 w-2 h-px bg-gray-300 dark:bg-gray-700"></div>

                                      <div className="pl-8 flex items-center gap-1.5 break-words">
                                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 inline-block">
                                          {tx.personnel || "—"}
                                        </div>
                                        {tx.position && tx.position !== "-" && (
                                          <span
                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium  max-w-max ${tx.position === "-" ? "" : "dark:text-blue-200 text-blue-800 dark:bg-blue-900  bg-blue-100"}`}
                                          >
                                            {getDisplayLabel(tx.position)}
                                          </span>
                                        )}
                                      </div>
                                    </td>

                                    <td className="py-1 px-2 align-middle">
                                      <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap">
                                        {tx.acquisition_from &&
                                        tx.acquisition_to ? (
                                          <div className="flex items-center gap-1">
                                            <span>
                                              {formatDate(tx.acquisition_from)}
                                            </span>
                                            <span className="opacity-50">
                                              -
                                            </span>
                                            <span>
                                              {formatDate(tx.acquisition_to)}
                                            </span>
                                          </div>
                                        ) : (
                                          "—"
                                        )}
                                      </div>
                                    </td>

                                    <td className="py-1 px-2 align-middle">
                                      <div
                                        className={`text-sm font-medium ${isAmountZero ? "text-gray-400 dark:text-gray-600" : "text-gray-800 dark:text-gray-200"}`}
                                      >
                                        {isAmountZero
                                          ? "—"
                                          : formatNumber(tx.amount)}
                                      </div>
                                    </td>

                                    <td className="py-1 px-2 align-middle">
                                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                        {tx.shares?.toLocaleString() || "0"}
                                      </div>
                                    </td>

                                    <td className="py-1 px-2 align-middle">
                                      <NetPercentBadge
                                        value={tx.holding_change}
                                        type={tx.transaction_type}
                                      />
                                    </td>

                                    <td className="py-1 px-3 align-middle">
                                      <button
                                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedSymbolName(
                                            trade.stock_symbol,
                                          );
                                          setAquisitionFromDate(
                                            tx.acquisition_from,
                                          );
                                          setSubListingTransactionType(
                                            tx.transaction_type,
                                          );
                                          setAquisitionToDate(
                                            tx.acquisition_to,
                                          );
                                          getCandleChartData(
                                            trade.stock_symbol,
                                          );
                                          setShowModal(true);
                                        }}
                                      >
                                        <ChartBar className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer" />
                                      </button>
                                    </td>
                                    <td className="py-1 px-2"></td>
                                  </tr>
                                );
                              })}
                          </>
                        )}
                    </React.Fragment>
                  );
                })
              )}
              {(hasMore || isLoading) && data.length > 0 && (
                <tr ref={observerTarget}>
                  <td colSpan={6} className="py-4">
                    {isLoading && (
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 dark:border-white"></div>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <span className="hidden">{String(darkMode)}</span>
      {showModal && (
        <InsiderChartModal
          isOpen={showModal}
          onClose={closeModal}
          stock={candledata}
          darkMode={darkMode}
          transactionType={subListingtransactionType}
          selectedSymbol={selectedSymbolName}
          aquisitionFromDate={aquisitionFromDate}
          aquisitionToDate={aquisitionToDate}
          candleLoading={candleLoading}
        />
      )}
    </div>
  );
};

export default SubstantialActivity;
